import { useAuthContext } from '../context/authContext';

/**
 * Custom hook for authentication
 * Provides easy access to authentication state and functions
 * 
 * @returns {object} Authentication state and functions
 */
const useAuth = () => {
  const auth = useAuthContext();

  // The signed-in user object carries a SINGULAR `role` (built in authService),
  // never a `roles` array — so the role helpers below used to read `user.roles`
  // and always return false (e.g. GenerateChallanModal's account-exec branch never
  // fired). Normalise to an array here, keeping a real `roles` array if one ever
  // appears. Permissions have no source in the token, so those helpers stay as-is.
  const userRoles = auth.user?.roles
    || (auth.user?.role != null ? [auth.user.role] : []);

  return {
    // Authentication state
    isAuthenticated: auth.isAuthenticated,
    isLoading: auth.loading,
    user: auth.user,
    error: auth.error,
    authState: auth.authState,

    // Derived state
    isLoggedIn: auth.isLoggedIn,
    currentUser: auth.currentUser,

    // Authentication functions
    login: auth.login,
    register: auth.register,
    logout: auth.logout,
    
    // User management functions
    updateUser: auth.updateUser,
    updatePassword: auth.updatePassword,
    
    // Password reset functions
    requestPasswordReset: auth.requestPasswordReset,
    resetPassword: auth.resetPassword,
    
    // Utility functions
    clearError: auth.clearError,

    // Helper functions
    /**
     * Helper group: role/permission checks and small utilities
     * These are convenience wrappers around `auth.user` fields to simplify
     * checks in UI components.
     */
    /**
     * Check if user has a specific role
     * @param {string} role - Role to check
     * @returns {boolean} True if user has the role
     */
    hasRole: (role) => {
      return userRoles.includes(role);
    },

    /**
     * Check if user has any of the specified roles
     * @param {string[]} roles - Array of roles to check
     * @returns {boolean} True if user has any of the roles
     */
    hasAnyRole: (roles) => {
      return roles.some(role => userRoles.includes(role));
    },

    /**
     * Check if user has all of the specified roles
     * @param {string[]} roles - Array of roles to check
     * @returns {boolean} True if user has all of the roles
     */
    hasAllRoles: (roles) => {
      return roles.every(role => userRoles.includes(role));
    },

    /**
     * Check if user has a specific permission
     * @param {string} permission - Permission to check
     * @returns {boolean} True if user has the permission
     */
    hasPermission: (permission) => {
      return auth.user?.permissions?.includes(permission) || false;
    },

    /**
     * Check if user has any of the specified permissions
     * @param {string[]} permissions - Array of permissions to check
     * @returns {boolean} True if user has any of the permissions
     */
    hasAnyPermission: (permissions) => {
      if (!auth.user?.permissions) return false;
      return permissions.some(permission => auth.user.permissions.includes(permission));
    },

    /**
     * Check if user has all of the specified permissions
     * @param {string[]} permissions - Array of permissions to check
     * @returns {boolean} True if user has all of the permissions
     */
    hasAllPermissions: (permissions) => {
      if (!auth.user?.permissions) return false;
      return permissions.every(permission => auth.user.permissions.includes(permission));
    },

    /**
     * Get user's full name
     * @returns {string} User's full name or email if name not available
     */
    getUserDisplayName: () => {
      if (auth.user?.firstName && auth.user?.lastName) {
        return `${auth.user.firstName} ${auth.user.lastName}`;
      }
      if (auth.user?.name) {
        return auth.user.name;
      }
      return auth.user?.email || 'User';
    },

    /**
     * Get user's initials for avatar
     * @returns {string} User's initials
     */
    getUserInitials: () => {
      if (auth.user?.firstName && auth.user?.lastName) {
        return `${auth.user.firstName.charAt(0)}${auth.user.lastName.charAt(0)}`.toUpperCase();
      }
      if (auth.user?.name) {
        const nameParts = auth.user.name.split(' ');
        if (nameParts.length >= 2) {
          return `${nameParts[0].charAt(0)}${nameParts[1].charAt(0)}`.toUpperCase();
        }
        return auth.user.name.charAt(0).toUpperCase();
      }
      if (auth.user?.email) {
        return auth.user.email.charAt(0).toUpperCase();
      }
      return 'U';
    },

    /**
     * Check if the current session is valid
     * @returns {boolean} True if session is valid
     */
    isSessionValid: () => {
      return auth.isAuthenticated && auth.user && !auth.error;
    },

    /**
     * Safely access nested user properties
     * @param {string} path - Dot notation path to the property
     * @param {*} defaultValue - Default value if property doesn't exist
     * @returns {*} Property value or default value
     */
    getUserProperty: (path, defaultValue = null) => {
      if (!auth.user) return defaultValue;
      
      const keys = path.split('.');
      let result = auth.user;
      
      for (const key of keys) {
        if (result && typeof result === 'object' && key in result) {
          result = result[key];
        } else {
          return defaultValue;
        }
      }
      
      return result;
    },
  };
};

export default useAuth;
